import { bornerPlaceDePapier, bornerTailleDePapier } from '@nextilt/commun';
import type { ChangementDePapier, Papier } from '../partage/api/types';

/**
 * Glisser un papier et tirer sa poignée, à l'ordinateur (chantier post-it-et-listes, ticket 03) :
 * les règles pures, sans React ni réseau, testées dans `tests/client/papiers/glisser.test.ts`.
 * Elles servent aux deux sortes de papier, post-it et liste libre (ticket 04).
 *
 * Décisions d'Igor du 2026-10-19 : on glisse un papier, jamais une bulle ; il reste où on l'a mis,
 * rien ne le pousse ; une poignée dans le coin bas droit règle largeur et hauteur, bornées ; si le
 * texte dépasse la hauteur choisie, le papier grandit. Les bornes sont celles de `commun/`, que
 * le serveur applique aussi : l'écran ne montre jamais une taille que le serveur ramènerait.
 *
 * La place d'un papier est celle de son **centre**, dans le repère du titre de la carte.
 */

export interface Point {
  x: number;
  y: number;
}

/** Un papier tel qu'il est posé : son centre et sa taille, sur la carte. */
export interface Cadre {
  x: number;
  y: number;
  largeur: number;
  hauteur: number;
}

/**
 * En pixels d'écran : sous ce mouvement, c'est un clic (on écrit dans le papier) ; au-delà, le
 * glisser part. Le même seuil que le glisser du fond de la carte.
 */
export const SEUIL_DU_GLISSER_PX = 5;

/** Le mouvement de la main, depuis l'appui, a-t-il passé le seuil ? */
export function aPasseLeSeuil(appui: Point, ici: Point): boolean {
  return Math.hypot(ici.x - appui.x, ici.y - appui.y) >= SEUIL_DU_GLISSER_PX;
}

/**
 * Où va un papier glissé : sa place de départ plus l'écart de la main, mesuré sur la carte.
 * Aucun mur autour des bulles, à tout zoom (retour d'Igor du 2026-10-20, choix B) : seules
 * restent les bornes de place du serveur. Il reste attrapable parce que le regard va jusqu'à lui
 * (`boitesDesPapiers`, lues par la borne du regard).
 */
export function placeGlissee(depart: Point, ecart: Point): Point {
  return bornerPlaceDePapier({ x: depart.x + ecart.x, y: depart.y + ecart.y });
}

/**
 * La place que prennent les papiers sur la carte, en boîtes autour de leur centre : la borne du
 * regard (`carte/borneDeLaCarte.ts`, `contourDesBoites`) les englobe avec les bulles, pour qu'on
 * puisse toujours glisser la carte jusqu'à un papier rangé loin (retour d'Igor du 2026-10-20).
 */
export function boitesDesPapiers(
  papiers: readonly Pick<Cadre, 'x' | 'y' | 'largeur' | 'hauteur'>[]
): { x: number; y: number; demiLargeur: number; demiHauteur: number }[] {
  return papiers.map((p) => ({ x: p.x, y: p.y, demiLargeur: p.largeur / 2, demiHauteur: p.hauteur / 2 }));
}

/**
 * La poignée du coin bas droit tirée de `ecart` (sur la carte) : la taille suit, bornée, et le
 * centre se déplace pour que le haut et la gauche du papier ne bougent pas (au demi-pixel près).
 *
 * `depart` : le papier tel qu'il se voit à l'appui (sa hauteur vue, texte compris).
 * `hauteurVue` : la hauteur qu'il prend une fois redessiné ; un texte plus haut que la hauteur
 * choisie le fait grandir vers le bas, et le centre se met sous le haut, à la moitié de ce qu'il
 * prend.
 */
export function cadreTire(depart: Cadre, ecart: Point, hauteurVue = 0): Cadre {
  const taille = bornerTailleDePapier({ largeur: depart.largeur + ecart.x, hauteur: depart.hauteur + ecart.y });
  const gauche = depart.x - depart.largeur / 2;
  const haut = depart.y - depart.hauteur / 2;
  // Au pixel entier, comme le serveur la garde : le papier ne saute pas d'un demi-pixel au retour.
  return {
    x: Math.round(gauche + taille.largeur / 2),
    y: Math.round(haut + Math.max(taille.hauteur, hauteurVue) / 2),
    ...taille,
  };
}

/** Ce qui part au serveur au lâcher : seulement ce qui a changé ; `null` si rien n'a bougé. */
export function changementDuGeste(avant: Cadre, apres: Cadre): ChangementDePapier | null {
  const changement: ChangementDePapier = {};
  for (const cle of ['x', 'y', 'largeur', 'hauteur'] as const) {
    if (apres[cle] !== avant[cle]) changement[cle] = apres[cle];
  }
  return Object.keys(changement).length > 0 ? changement : null;
}

/**
 * Un enregistrement refusé : le papier reprend, pour les seuls champs envoyés, ce qu'il avait
 * avant. Le reste (un texte écrit entre-temps) n'est pas touché.
 */
export function remettreAvant(papiers: readonly Papier[], avant: Papier, envoye: ChangementDePapier): Papier[] {
  return papiers.map((p) => {
    if (p.id !== avant.id) return p;
    const repris: Papier = { ...p };
    for (const cle of Object.keys(envoye) as (keyof ChangementDePapier)[]) {
      Object.assign(repris, { [cle]: avant[cle] });
    }
    return repris;
  });
}
